import type { LocationQuery } from 'vue-router';

import { tryOnScopeDispose, tryOnUnmounted } from '@vueuse/core';
import snakeCase from 'lodash/snakeCase';
import { computed, nextTick, readonly, ref } from 'vue';
import { isNavigationFailure, useRoute, useRouter } from 'vue-router';

import { startBrandSwitchVital } from '@/composables/brandSwitchVital';
import { getRouteBrandLabel } from '@/router/brand';
import { useAuthStore } from '@/stores/auth';
import { useTrackingStore } from '@/stores/tracking';

type BeforeBrandSwitchGuard = () => boolean | Promise<boolean>;

const copyQueryParams = ref(false);
const includeQueryParams = ref<string[]>([]);
const excludeQueryParams = ref<string[]>([]);
const isBrandSwitching = ref(false);
const beforeBrandSwitchGuards = new Set<BeforeBrandSwitchGuard>();
let activeSwitch: { brandLabel: string } | null = null;

/**
 * Brand the switcher is navigating to, or null when no switch is in flight. The router guard
 * rewrites navigations started mid-switch (brand watchers syncing query params, page redirects)
 * onto this brand, so they don't cancel the switch and land back on the old brand.
 */
export function brandSwitchTarget(): string | null {
  return activeSwitch?.brandLabel ?? null;
}

/**
 * Raised by the router guard while a navigation to a brand-prefixed URL of a
 * different brand is in flight (e.g. a Vision AI redirect widget). Brand
 * watchers that react with their own navigations check `isBrandSwitching` and
 * stay quiet, so they don't cancel the in-flight navigation.
 */
export function setNavigationBrandSwitch(value: boolean) {
  isBrandSwitching.value = value;
}

const includeQueryParamsToCheck = computed(() =>
  includeQueryParams.value.flatMap((param) => [param, snakeCase(param)]),
);

const excludeQueryParamsToCheck = computed(() =>
  excludeQueryParams.value.flatMap((param) => [param, snakeCase(param)]),
);

function enableCopyQueryParams({
  include,
  exclude,
}: { include?: string[]; exclude?: string[] } = {}) {
  copyQueryParams.value = true;
  includeQueryParams.value = Array.isArray(include) ? include : [];
  excludeQueryParams.value = Array.isArray(exclude) ? exclude : [];
}

function filterQueryParams(query: LocationQuery = {}) {
  if (!copyQueryParams.value) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(query).filter(
      ([param]) =>
        (!includeQueryParamsToCheck.value?.length ||
          includeQueryParamsToCheck.value.includes(param)) &&
        !(excludeQueryParamsToCheck.value ?? []).includes(param),
    ),
  );
}

/** Runs the registered before-switch guards; `false` when any vetoes the brand change. */
export async function runBeforeBrandSwitchGuards(): Promise<boolean> {
  for (const guard of beforeBrandSwitchGuards) {
    if (!(await guard())) return false;
  }
  return true;
}

/**
 * Vetoes brand changes while the caller's scope lives: the switcher and URL-driven brand changes
 * (links, back/forward) both run it. Returns an unregister fn.
 */
export function registerBeforeBrandSwitch(guard: BeforeBrandSwitchGuard): () => void {
  beforeBrandSwitchGuards.add(guard);
  const unregister = () => beforeBrandSwitchGuards.delete(guard);
  tryOnScopeDispose(unregister);
  return unregister;
}

export function useBrandSwitcher() {
  const route = useRoute();
  const router = useRouter();
  const authStore = useAuthStore();
  const trackingStore = useTrackingStore();

  async function performBrandSwitch(brandLabel: string, query?: LocationQuery) {
    const thisSwitch = { brandLabel };
    activeSwitch = thisSwitch;
    isBrandSwitching.value = true;
    const stopVital = startBrandSwitchVital({ routeName: String(route.name), storeOnly: false });

    try {
      const result = await router.push({
        name: route.name,
        params: { brandLabel },
        query,
      });

      // The router guard normally sets the store; it skips navigations from a dashboard.
      if (!isNavigationFailure(result) && authStore.currentBrandLabel !== brandLabel) {
        void authStore.setCurrentBrand({ brandLabel });
      }

      void stopVital(isNavigationFailure(result) ? 'aborted' : 'completed');
      return result;
    } catch (error) {
      void stopVital('failed');
      throw error;
    } finally {
      // A newer switch owns the flag now.
      if (activeSwitch === thisSwitch) {
        activeSwitch = null;
        isBrandSwitching.value = false;
      }
    }
  }

  async function switchBrand(brandLabel: string) {
    if (!(await runBeforeBrandSwitchGuards())) {
      return;
    }

    if (getRouteBrandLabel(route)) {
      await performBrandSwitch(brandLabel, filterQueryParams(route.query));
    } else {
      const stopVital = startBrandSwitchVital({ routeName: String(route.name), storeOnly: true });
      void authStore.setCurrentBrand({ brandLabel });
      void stopVital('completed');
      void nextTick(() => {
        // faking a Page Viewed event to keep metrics consistent
        trackingStore.trackPageViewedEvent(route, null);
      });
    }
  }

  tryOnUnmounted(() => {
    copyQueryParams.value = false;
    includeQueryParams.value = [];
    excludeQueryParams.value = [];
  });

  return {
    isBrandSwitching: readonly(isBrandSwitching),
    enableCopyQueryParams,
    filterQueryParams,
    registerBeforeBrandSwitch,
    switchBrand,
  };
}
